import * as React from "react";
import { Ban, CircleAlert, CircleCheck, CircleX, LucideIcon } from "lucide-react";
import { HospitalSymbol } from "src/components/icons/custom";
import { Icon } from "src/components/icons/Icon";

export type RecommendationStatus =
	| CoreScoringDecisionType
	| OnroadRecommendationType
	| CareScoringDecisionType
	| VitalsScoringDecisionType;

interface StatusStyle {
	icon: LucideIcon;
	color: string;
	title: string;
	/** filled badge (every status but Aborted, which is a bare glyph) */
	badge?: boolean;
}

const GREEN = "#027F01";
const RED = "#FA0B02";
const AMBER = "#FCA900";

const STATUS_STYLES: Record<RecommendationStatus, StatusStyle> = {
	Pass: { icon: CircleCheck, color: GREEN, title: "Pass", badge: true },
	LowRisk: { icon: CircleCheck, color: GREEN, title: "Low Risk", badge: true },
	Fail: { icon: CircleX, color: RED, title: "Fail", badge: true },
	HighRisk: { icon: CircleX, color: RED, title: "High Risk", badge: true },
	RangeOfInquiry: { icon: CircleAlert, color: AMBER, title: "Range of Inquiry", badge: true },
	Hazardous: { icon: HospitalSymbol, color: RED, title: "Hazardous", badge: true },
	Aborted: { icon: Ban, color: "#092a58", title: "Aborted" },
};

export const StatusIcon: React.FC<{ status: RecommendationStatus }> = ({ status }) => {
	const style = STATUS_STYLES[status];
	if (!style) return null;
	return (
		<Icon
			icon={style.icon}
			badge={style.badge}
			size="2x"
			title={style.title}
			// centred on the row's text; the negative margin keeps the 2em badge from
			// making its row taller than the text-only rows
			style={{ color: style.color, verticalAlign: "middle", margin: "-0.5em 0" }}
		/>
	);
};

interface RecommendationCellProps {
	status: RecommendationStatus | undefined;
	dateCompleted: unknown;
	testId: number | undefined;
}

/** Results-grid cell: the status icon, else C (completed) / - (no test) / NC. */
export const RecommendationCell: React.FC<RecommendationCellProps> = ({
	status,
	dateCompleted,
	testId,
}) => {
	if (status) return <StatusIcon status={status} />;
	if (dateCompleted) return <>C</>;
	if (!testId) return <>-</>;
	return <>NC</>;
};
